import { createMemo, createResource, Show } from "solid-js";
import type { MarketPrice } from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatNumber, formatTime } from "../format.ts";
import { Chart, type ChartSeries } from "./Chart.tsx";
import { Skeleton } from "./ui.tsx";

const HOUR = 3_600_000;

/**
 * Day-ahead prices from TGE (Fixing II), hourly for today and tomorrow once published, with the RCE settlement price
 * where it is known. The same for every place: the market price, before any margin of the seller.
 */
export function MarketPrices() {
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  const to = new Date(from.getTime() + 2 * 24 * HOUR);
  const [prices] = createResource(() => api.market.prices(from, to, "hour"));
  const now = () =>
    (prices() ?? []).find(
      (p) =>
        new Date(p.start).getTime() <= Date.now() &&
        Date.now() < new Date(p.start).getTime() + HOUR,
    );
  const today = () =>
    (prices() ?? []).filter(
      (p) => new Date(p.start).getTime() < from.getTime() + 24 * HOUR,
    );
  const extreme = (pick: (a: number, b: number) => boolean) =>
    today().reduce<MarketPrice | undefined>(
      (best, p) => (best === undefined || pick(p.price, best.price) ? p : best),
      undefined,
    );
  const series = createMemo<ChartSeries[]>(() => {
    const list = prices() ?? [];
    const out: ChartSeries[] = [
      {
        key: "price",
        label: t("market.price"),
        color: "var(--accent)",
        kind: "bar",
        points: list.map((p) => ({
          t: new Date(p.start).getTime(),
          v: p.price,
        })),
      },
    ];
    if (list.some((p) => p.rce !== null)) {
      out.push({
        key: "rce",
        label: t("market.rce"),
        color: "var(--warn)",
        kind: "line",
        points: list.map((p) => ({
          t: new Date(p.start).getTime() + HOUR / 2,
          v: p.rce,
        })),
      });
    }
    return out;
  });
  const money = (v: number) =>
    `${formatNumber(v, locale(), 0)} ${t("market.unit")}`;
  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("market.title")}</h2>
        <span class="meta">{t("market.meta")}</span>
      </div>
      <div class="card-b">
        <Show when={prices()} fallback={<Skeleton lines={4} />}>
          <Show
            when={(prices() ?? []).length > 0}
            fallback={
              <p class="hint" style={{ margin: 0 }}>
                {t("market.none")}
              </p>
            }
          >
            <dl class="kv" style={{ "margin-top": 0 }}>
              <dt>{t("market.now")}</dt>
              <dd class="num">{now() ? money(now()!.price) : "—"}</dd>
              <Show when={extreme((a, b) => a < b)}>
                {(lo) => (
                  <>
                    <dt>{t("market.lowest")}</dt>
                    <dd class="num">
                      {money(lo().price)}{" "}
                      <span class="hint">
                        {formatTime(lo().start, locale())}
                      </span>
                    </dd>
                  </>
                )}
              </Show>
              <Show when={extreme((a, b) => a > b)}>
                {(hi) => (
                  <>
                    <dt>{t("market.highest")}</dt>
                    <dd class="num">
                      {money(hi().price)}{" "}
                      <span class="hint">
                        {formatTime(hi().start, locale())}
                      </span>
                    </dd>
                  </>
                )}
              </Show>
            </dl>
            <Chart
              series={series()}
              from={from}
              to={to}
              unit={t("market.unit")}
              locale={locale()}
              bucketMs={HOUR}
              emptyText={t("market.none")}
              height={180}
            />
          </Show>
        </Show>
      </div>
    </section>
  );
}
